// frontend/app/(facility)/compliance/page.tsx
"use client";

import { useState } from "react";
import { AlertTriangle, CheckCircle, XCircle, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useFacilityComplianceSummary,
  useFacilityComplianceApplications,
  useFacilityComplianceContracts,
  ComplianceFilter,
  ApplicationComplianceItem,
  ContractComplianceItem,
} from "@/lib/hooks/useFacilityCompliance";

// =============================================================================
// Filter Tabs Component
// =============================================================================

interface FilterTabsProps {
  value: ComplianceFilter;
  onChange: (value: ComplianceFilter) => void;
}

function FilterTabs({ value, onChange }: FilterTabsProps) {
  const options: ComplianceFilter[] = ["ALL", "COMPLIANT", "AT_RISK", "NON_COMPLIANT"];

  return (
    <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
      {options.map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onChange(option)}
          className={cn(
            "rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
            value === option
              ? "bg-slate-900 text-white"
              : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
          )}
        >
          {option === "ALL"
            ? "All"
            : option === "AT_RISK"
            ? "At Risk"
            : option === "NON_COMPLIANT"
            ? "Non-Compliant"
            : "Compliant"}
        </button>
      ))}
    </div>
  );
}

// =============================================================================
// Summary Card Component
// =============================================================================

interface SummaryCardProps {
  label: string;
  value: number;
  subtitle?: string;
  severity?: "success" | "warning" | "danger";
}

function SummaryCard({ label, value, subtitle, severity }: SummaryCardProps) {
  const Icon =
    severity === "danger"
      ? XCircle
      : severity === "warning"
      ? AlertTriangle
      : CheckCircle;

  return (
    <div
      className={cn(
        "rounded-lg border p-4",
        severity === "danger" && "border-red-200 bg-red-50",
        severity === "warning" && "border-amber-200 bg-amber-50",
        severity === "success" && "border-emerald-200 bg-emerald-50",
        !severity && "border-slate-200 bg-white"
      )}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
            {label}
          </div>
          <div className="mt-2 text-3xl font-bold text-slate-900">{value}</div>
          {subtitle && (
            <div className="mt-1 text-xs text-slate-600">{subtitle}</div>
          )}
        </div>
        {severity && (
          <Icon
            className={cn(
              "h-5 w-5",
              severity === "danger" && "text-red-500",
              severity === "warning" && "text-amber-500",
              severity === "success" && "text-emerald-500"
            )}
          />
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Compliance Badge Component
// =============================================================================

function ComplianceBadge({
  status,
}: {
  status: "COMPLIANT" | "AT_RISK" | "NON_COMPLIANT";
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
        status === "COMPLIANT" && "bg-emerald-100 text-emerald-700",
        status === "AT_RISK" && "bg-amber-100 text-amber-700",
        status === "NON_COMPLIANT" && "bg-red-100 text-red-700"
      )}
    >
      {status === "COMPLIANT" && <CheckCircle className="h-3 w-3" />}
      {status === "AT_RISK" && <AlertTriangle className="h-3 w-3" />}
      {status === "NON_COMPLIANT" && <XCircle className="h-3 w-3" />}
      {status === "COMPLIANT"
        ? "Compliant"
        : status === "AT_RISK"
        ? "At Risk"
        : "Non-Compliant"}
    </span>
  );
}

// =============================================================================
// Issues List Component
// =============================================================================

function IssuesList({ issues }: { issues: { credential_type_name: string; reason: string }[] }) {
  if (issues.length === 0) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-1">
      {issues.map((issue, idx) => (
        <span
          key={idx}
          className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-[0.65rem] text-slate-600"
        >
          {issue.credential_type_name}: {issue.reason.toLowerCase().replace("_", " ")}
        </span>
      ))}
    </div>
  );
}

// =============================================================================
// Applications Table Component
// =============================================================================

interface ApplicationsTableProps {
  applications: ApplicationComplianceItem[];
  isLoading: boolean;
}

function ApplicationsTable({ applications, isLoading }: ApplicationsTableProps) {
  if (isLoading) {
    return (
      <div className="py-8 text-center text-sm text-slate-500">
        Loading applications...
      </div>
    );
  }

  if (applications.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-slate-500">
        No applications found for this filter.
      </div>
    );
  }

  return (
    <div className="divide-y divide-slate-100">
      {applications.map((app) => (
        <div key={app.application_id} className="py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-900">{app.nurse_name}</span>
                <ComplianceBadge status={app.compliance_status} />
              </div>
              <div className="mt-1 text-sm text-slate-600">
                {app.job_title} &middot; {app.job_specialty}
              </div>
              <div className="mt-0.5 text-xs text-slate-500">
                Status: {app.application_status} &middot; {app.job_state}
              </div>
              <IssuesList issues={app.issues} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// Contracts Table Component
// =============================================================================

interface ContractsTableProps {
  contracts: ContractComplianceItem[];
  isLoading: boolean;
}

function ContractsTable({ contracts, isLoading }: ContractsTableProps) {
  if (isLoading) {
    return (
      <div className="py-8 text-center text-sm text-slate-500">
        Loading contracts...
      </div>
    );
  }

  if (contracts.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-slate-500">
        No contracts found for this filter.
      </div>
    );
  }

  return (
    <div className="divide-y divide-slate-100">
      {contracts.map((contract) => (
        <div key={contract.contract_id} className="py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-900">
                  {contract.nurse_name}
                </span>
                <ComplianceBadge status={contract.compliance_status} />
              </div>
              <div className="mt-1 text-sm text-slate-600">
                {contract.job_title} &middot; {contract.job_specialty}
              </div>
              <div className="mt-0.5 text-xs text-slate-500">
                {contract.contract_start_date} - {contract.contract_end_date} &middot;{" "}
                {contract.contract_status}
              </div>
              <IssuesList issues={contract.issues} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// Main Page Component
// =============================================================================

export default function FacilityCompliancePage() {
  const [filter, setFilter] = useState<ComplianceFilter>("ALL");

  const {
    data: summary,
    isLoading: summaryLoading,
    refetch: refetchSummary,
  } = useFacilityComplianceSummary();

  const {
    data: applicationsData,
    isLoading: applicationsLoading,
    refetch: refetchApplications,
  } = useFacilityComplianceApplications({
    page: 1,
    pageSize: 10,
    complianceFilter: filter,
    statusScope: "PIPELINE",
  });

  const {
    data: contractsData,
    isLoading: contractsLoading,
    refetch: refetchContracts,
  } = useFacilityComplianceContracts({
    page: 1,
    pageSize: 10,
    complianceFilter: filter,
    statusScope: "ACTIVE",
  });

  const handleRefresh = () => {
    refetchSummary();
    refetchApplications();
    refetchContracts();
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Header */}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Compliance Overview
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            See how your current candidates and contracts align with credential
            requirements.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={handleRefresh}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
          <FilterTabs value={filter} onChange={setFilter} />
        </div>
      </header>

      {/* Summary Cards */}
      <section className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {summaryLoading || !summary ? (
          <div className="col-span-full py-4 text-center text-sm text-slate-500">
            Loading compliance summary...
          </div>
        ) : (
          <>
            <SummaryCard
              label="Active Contracts"
              value={summary.contracts.total_active}
              subtitle={`${summary.contracts.compliant} compliant`}
            />
            <SummaryCard
              label="Compliant Contracts"
              value={summary.contracts.compliant}
              severity="success"
            />
            <SummaryCard
              label="At-Risk Contracts"
              value={summary.contracts.at_risk}
              severity="warning"
            />
            <SummaryCard
              label="Non-Compliant"
              value={summary.contracts.non_compliant}
              severity="danger"
            />
          </>
        )}
      </section>

      {/* Top Issues */}
      {!summaryLoading && summary && (
        <section className="mt-8">
          <div className="rounded-lg border border-slate-200 bg-white p-6">
            <h2 className="text-lg font-semibold text-slate-900">
              Top Compliance Issues
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Credential types most frequently blocking hires.
            </p>

            {summary.top_issues.missing_credentials.length === 0 &&
            summary.top_issues.expired_credentials.length === 0 &&
            summary.top_issues.not_verified.length === 0 ? (
              <div className="mt-4 text-sm text-slate-500">
                No compliance issues detected.
              </div>
            ) : (
              <div className="mt-4 grid gap-4 sm:grid-cols-3">
                {summary.top_issues.missing_credentials.length > 0 && (
                  <div>
                    <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Missing
                    </h3>
                    <ul className="mt-2 space-y-1">
                      {summary.top_issues.missing_credentials.slice(0, 5).map((issue) => (
                        <li
                          key={issue.credential_type_id}
                          className="flex items-center justify-between text-sm"
                        >
                          <span className="text-slate-700">
                            {issue.credential_type_name}
                          </span>
                          <span className="text-slate-500">{issue.count}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {summary.top_issues.expired_credentials.length > 0 && (
                  <div>
                    <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Expired
                    </h3>
                    <ul className="mt-2 space-y-1">
                      {summary.top_issues.expired_credentials.slice(0, 5).map((issue) => (
                        <li
                          key={issue.credential_type_id}
                          className="flex items-center justify-between text-sm"
                        >
                          <span className="text-slate-700">
                            {issue.credential_type_name}
                          </span>
                          <span className="text-slate-500">{issue.count}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {summary.top_issues.not_verified.length > 0 && (
                  <div>
                    <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Not Verified
                    </h3>
                    <ul className="mt-2 space-y-1">
                      {summary.top_issues.not_verified.slice(0, 5).map((issue) => (
                        <li
                          key={issue.credential_type_id}
                          className="flex items-center justify-between text-sm"
                        >
                          <span className="text-slate-700">
                            {issue.credential_type_name}
                          </span>
                          <span className="text-slate-500">{issue.count}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Applications & Contracts */}
      <section className="mt-8 grid gap-6 lg:grid-cols-2">
        {/* Applications */}
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-slate-900">
            Applications in Pipeline
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Active applications and their compliance status.
          </p>
          <div className="mt-4">
            <ApplicationsTable
              applications={applicationsData?.items ?? []}
              isLoading={applicationsLoading}
            />
          </div>
        </div>

        {/* Contracts */}
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-slate-900">
            Active Contracts
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Current contracts and their compliance status.
          </p>
          <div className="mt-4">
            <ContractsTable
              contracts={contractsData?.items ?? []}
              isLoading={contractsLoading}
            />
          </div>
        </div>
      </section>
    </div>
  );
}
